import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import {
  useContactSubmissions,
  useContactStats,
  useUpdateContactStatus,
  useDeleteContactSubmission,
  type ContactSubmission,
  type ContactStatus,
  type ContactSubmissionId,
} from '@/hooks'

/**
 * Contact submissions management page.
 * Displays all contact form submissions with the ability to mark as read/unread and delete.
 */
export function Contact() {
  const [statusFilter, setStatusFilter] = useState<ContactStatus | undefined>(undefined)
  const [selectedId, setSelectedId] = useState<ContactSubmissionId | null>(null)

  const { data: submissions, isLoading } = useContactSubmissions(statusFilter)
  const { data: stats } = useContactStats()
  const { mutate: updateStatus } = useUpdateContactStatus()
  const { mutate: deleteSubmission } = useDeleteContactSubmission()

  // Derive selected submission from the real-time data to ensure it stays in sync
  const selectedSubmission = useMemo(() => {
    if (!selectedId || !submissions) return null
    return submissions.find((s) => s._id === selectedId) ?? null
  }, [selectedId, submissions])

  const handleMarkAsRead = async (submission: ContactSubmission) => {
    try {
      await updateStatus(submission._id, 'read')
    } catch (error) {
      console.error('Failed to mark as read:', error)
    }
  }

  const handleMarkAsUnread = async (submission: ContactSubmission) => {
    try {
      await updateStatus(submission._id, 'unread')
    } catch (error) {
      console.error('Failed to mark as unread:', error)
    }
  }

  const handleDelete = async (submission: ContactSubmission) => {
    if (!confirm('Are you sure you want to delete this message?')) return

    try {
      await deleteSubmission(submission._id)
      if (selectedId === submission._id) {
        setSelectedId(null)
      }
    } catch (error) {
      console.error('Failed to delete:', error)
    }
  }

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Mensajes de Contacto</h1>
          <p className="mt-1 text-muted-foreground">
            Gestiona las consultas recibidas desde el formulario de contacto.
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="rounded-lg border bg-card p-4 text-card-foreground shadow-sm">
          <div className="text-2xl font-bold">{stats?.total ?? 0}</div>
          <div className="text-sm text-muted-foreground">Total mensajes</div>
        </div>
        <div
          className={`cursor-pointer rounded-lg border p-4 shadow-sm transition-colors ${
            statusFilter === 'unread'
              ? 'border-primary bg-primary/10'
              : 'bg-card hover:border-primary/50'
          }`}
          onClick={() => setStatusFilter(statusFilter === 'unread' ? undefined : 'unread')}
        >
          <div className="flex items-center gap-2">
            <div className="text-2xl font-bold text-amber-600">{stats?.unread ?? 0}</div>
            {(stats?.unread ?? 0) > 0 && (
              <span className="flex h-2 w-2 rounded-full bg-amber-500" />
            )}
          </div>
          <div className="text-sm text-muted-foreground">Sin leer</div>
        </div>
        <div
          className={`cursor-pointer rounded-lg border p-4 shadow-sm transition-colors ${
            statusFilter === 'read'
              ? 'border-primary bg-primary/10'
              : 'bg-card hover:border-primary/50'
          }`}
          onClick={() => setStatusFilter(statusFilter === 'read' ? undefined : 'read')}
        >
          <div className="text-2xl font-bold text-green-600">{stats?.read ?? 0}</div>
          <div className="text-sm text-muted-foreground">Leidos</div>
        </div>
      </div>

      {/* Filter indicator */}
      {statusFilter && (
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">
            Filtrando por: <span className="font-medium">{statusFilter === 'unread' ? 'Sin leer' : 'Leidos'}</span>
          </span>
          <button
            onClick={() => setStatusFilter(undefined)}
            className="text-sm text-primary hover:underline"
          >
            Limpiar filtro
          </button>
        </div>
      )}

      {/* Content */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Submissions List */}
        <div className="space-y-3">
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-24 animate-pulse rounded-lg border bg-muted" />
              ))}
            </div>
          ) : submissions?.length === 0 ? (
            <div className="rounded-lg border bg-card p-8 text-center text-card-foreground">
              <MailIcon className="mx-auto h-12 w-12 text-muted-foreground" />
              <p className="mt-4 text-muted-foreground">
                {statusFilter
                  ? `No hay mensajes ${statusFilter === 'unread' ? 'sin leer' : 'leidos'}.`
                  : 'No hay mensajes de contacto.'}
              </p>
            </div>
          ) : (
            submissions?.map((submission) => (
              <div
                key={submission._id}
                onClick={() => {
                  setSelectedId(submission._id)
                  if (submission.status === 'unread') {
                    handleMarkAsRead(submission)
                  }
                }}
                className={`cursor-pointer rounded-lg border p-4 transition-colors ${
                  selectedId === submission._id
                    ? 'border-primary bg-primary/5'
                    : 'bg-card hover:border-primary/50'
                } ${submission.status === 'unread' ? 'border-l-4 border-l-amber-500' : ''}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold truncate">{submission.name}</span>
                      {submission.status === 'unread' && (
                        <span className="flex h-2 w-2 rounded-full bg-amber-500" />
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground truncate">{submission.email}</div>
                    <p className="mt-1 line-clamp-2 text-sm">{submission.message}</p>
                    {submission.crm_state && <p className={`mt-2 text-xs ${submission.crm_state === 'review' ? 'text-amber-700' : 'text-muted-foreground'}`}>
                      {submission.crm_state === 'linked' ? `Bitrix · Lead ${submission.crm_lead_id}` : submission.crm_state === 'review' ? 'Bitrix · Revisión de entrega necesaria' : 'Bitrix · Entrega pendiente'}
                    </p>}
                  </div>
                  <div className="text-right text-xs text-muted-foreground whitespace-nowrap">
                    {formatDate(submission.created_at)}
                  </div>
                </div>
                {submission.property && (
                  <div className="mt-2">
                    <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                      {submission.property.title}
                    </span>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Detail Panel */}
        <div className="lg:sticky lg:top-6">
          {selectedSubmission ? (
            <div className="rounded-lg border bg-card p-6 text-card-foreground shadow-sm">
              <div className="mb-4 flex items-start justify-between">
                <div>
                  <h2 className="text-xl font-semibold">{selectedSubmission.name}</h2>
                  {selectedSubmission.email && <a
                    href={`mailto:${selectedSubmission.email}`}
                    className="text-primary hover:underline"
                  >
                    {selectedSubmission.email}
                  </a>}
                  {selectedSubmission.phone && (
                    <div className="mt-1">
                      <a
                        href={`tel:${selectedSubmission.phone}`}
                        className="text-sm text-muted-foreground hover:text-foreground"
                      >
                        {selectedSubmission.phone}
                      </a>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                      selectedSubmission.status === 'unread'
                        ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400'
                        : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                    }`}
                  >
                    {selectedSubmission.status === 'unread' ? 'Sin leer' : 'Leido'}
                  </span>
                </div>
              </div>

              <div className="mb-4 text-xs text-muted-foreground">
                Recibido: {formatDate(selectedSubmission.created_at)}
              </div>
              {selectedSubmission.crm_state && <div className="mb-4 rounded-lg border p-3 text-sm">
                {selectedSubmission.crm_lead_id ? <a className="text-primary hover:underline" href={`https://b24-vg2xjj.bitrix24.eu/crm/lead/details/${selectedSubmission.crm_lead_id}/`} target="_blank" rel="noopener noreferrer">Abrir Lead {selectedSubmission.crm_lead_id} en Bitrix</a> : <p role={selectedSubmission.crm_state === 'review' ? 'alert' : undefined}>{selectedSubmission.crm_state === 'review' ? 'La entrega requiere revisión. La consulta está guardada; no crees otro lead hasta comprobar si ya existe en Bitrix.' : 'La consulta está guardada y pendiente de entrega a Bitrix.'}</p>}
                {selectedSubmission.attribution?.utm_campaign && <p className="mt-2">Campaña: {selectedSubmission.attribution.utm_campaign}</p>}
              </div>}

              {selectedSubmission.property && (
                <div className="mb-4 rounded-lg bg-muted/50 p-3">
                  <div className="text-xs font-medium text-muted-foreground">
                    Consulta sobre propiedad:
                  </div>
                  <Link
                    to={`/properties/${selectedSubmission.property.id}/edit`}
                    className="font-medium text-primary hover:underline"
                  >
                    {selectedSubmission.property.title}
                  </Link>
                </div>
              )}

              <div className="mb-6">
                <div className="mb-2 text-sm font-medium">Mensaje:</div>
                <div className="whitespace-pre-wrap rounded-lg bg-muted/50 p-4 text-sm">
                  {selectedSubmission.message}
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                {selectedSubmission.email && <a
                  href={`mailto:${selectedSubmission.email}?subject=Re: Consulta A1Homes`}
                  className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
                >
                  <MailIcon className="mr-2 h-4 w-4" />
                  Responder
                </a>}
                {selectedSubmission.status === 'unread' ? (
                  <button
                    onClick={() => handleMarkAsRead(selectedSubmission)}
                    className="inline-flex items-center justify-center rounded-md border bg-background px-4 py-2 text-sm font-medium hover:bg-muted"
                  >
                    <CheckIcon className="mr-2 h-4 w-4" />
                    Marcar como leido
                  </button>
                ) : (
                  <button
                    onClick={() => handleMarkAsUnread(selectedSubmission)}
                    className="inline-flex items-center justify-center rounded-md border bg-background px-4 py-2 text-sm font-medium hover:bg-muted"
                  >
                    <UnreadIcon className="mr-2 h-4 w-4" />
                    Marcar como no leido
                  </button>
                )}
                <button
                  onClick={() => handleDelete(selectedSubmission)}
                  disabled={!!selectedSubmission.crm_state && selectedSubmission.crm_state !== 'linked'}
                  className="inline-flex items-center justify-center rounded-md border border-red-200 bg-background px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 dark:border-red-800 dark:hover:bg-red-900/20"
                >
                  <TrashIcon className="mr-2 h-4 w-4" />
                  Eliminar
                </button>
              </div>
            </div>
          ) : (
            <div className="rounded-lg border bg-card p-8 text-center text-card-foreground">
              <MailIcon className="mx-auto h-12 w-12 text-muted-foreground" />
              <p className="mt-4 text-muted-foreground">
                Selecciona un mensaje para ver los detalles.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// Icons
function MailIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <rect width="20" height="16" x="2" y="4" rx="2" />
      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
    </svg>
  )
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  )
}

function UnreadIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <circle cx="12" cy="12" r="10" />
    </svg>
  )
}

function TrashIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M3 6h18" />
      <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
      <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
      <line x1="10" x2="10" y1="11" y2="17" />
      <line x1="14" x2="14" y1="11" y2="17" />
    </svg>
  )
}
